/**
 * Clients — group `Work[]` by `clientName` and produce display rows.
 *
 * Mirrors the "ver por cliente" filter that lives inside
 * `mobile/src/screens/WorkListScreen.tsx` at the time of writing.
 * The logic is split out so the route surface (`/clients`) stays
 * JSX-only and the grouping math is testable in isolation under
 * vitest without spinning up React Testing Library.
 *
 * Grouping contract:
 *   - Key: NFC-normalized + trimmed + lowercased `clientName`.
 *     "Pepe Pérez" and "pepe pérez " collapse to the same entry.
 *     Mobile uses the same heuristic in its filter.
 *   - Display name: the casing of the FIRST work in the group as
 *     iterated (insertion order). Future enhancement: pick the
 *     longest casing — for now insertion order is deterministic
 *     because callers pass a consistent sort upstream.
 *   - Empty `clientName` (undefined / empty / whitespace) is
 *     EXCLUDED. Works without a client never appear on /clients.
 *     The user sees them on /works (the default WorkList) instead.
 *   - Archived works are EXCLUDED so the totals match the
 *     "active backlog per client" metric an admin reasons about.
 *
 * Slug derivation (for URL drilldown `/clients/:slug`):
 *   - Lowercased + NFC + replace non-alphanumeric with `-` +
 *     collapse runs of `-` + trim leading/trailing `-`.
 *   - Slug collisions are resolved by appending `-2`, `-3`, … to
 *     the second+ occurrence. Stable across reloads as long as the
 *     same input order is passed.
 *
 * Phone formatting:
 *   - Returns the first non-empty `clientPhone` from the group.
 *   - The `tel:` link uses the raw value (already validated at
 *     write-time to be E.164 by the spec 219 phone input).
 *
 * Address:
 *   - First non-empty `clientAddress` wins. The drilldown shows it
 *     verbatim; copying to clipboard happens at the route level.
 */
import type { Client, Work } from '../types';

export interface ClientGroup {
  /** Lowercased + trimmed + NFC. Unique per group. */
  key: string;
  /** URL slug, NFC-stripped. Unique across the result. */
  slug: string;
  /** Display name with original casing (insertion-order winner). */
  name: string;
  /** First non-empty phone in the group, or `''`. */
  phone: string;
  /** First non-empty address in the group, or `''`. */
  address: string;
  /** First non-empty NIF/CIF/etc., or `''`. */
  taxId: string;
  /** Active (non-archived) works in this group. */
  works: Work[];
  /** `works.length` — convenience. */
  total: number;
  /** Most-recent `updatedAt` across `works`. Always > 0. */
  lastUpdatedAt: number;
}

export function normalizeClientKey(name: string | undefined): string {
  if (!name) return '';
  return name.normalize('NFC').trim().toLocaleLowerCase('es');
}

export function clientSlug(name: string): string {
  return name
    .normalize('NFC')
    .toLocaleLowerCase('es')
    // Replace accented vowels and ñ with their ASCII equivalents so the
    // slug is URL-safe across browsers that don't IRI-encode in the
    // address bar.
    .replace(/[áàâä]/g, 'a')
    .replace(/[éèêë]/g, 'e')
    .replace(/[íìîï]/g, 'i')
    .replace(/[óòôö]/g, 'o')
    .replace(/[úùûü]/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function groupClients(allWorks: ReadonlyArray<Work>): ClientGroup[] {
  const byKey = new Map<string, ClientGroup>();
  const slugSeen = new Map<string, number>();

  for (const w of allWorks) {
    if (w.archived) continue;
    const rawName = w.clientName?.trim();
    if (!rawName) continue;
    const key = normalizeClientKey(rawName);
    if (!key) continue;

    let group = byKey.get(key);
    if (!group) {
      const baseSlug = clientSlug(rawName) || 'cliente';
      const occurrence = (slugSeen.get(baseSlug) ?? 0) + 1;
      slugSeen.set(baseSlug, occurrence);
      const slug = occurrence === 1 ? baseSlug : `${baseSlug}-${occurrence}`;

      group = {
        key,
        slug,
        name: rawName,
        phone: '',
        address: '',
        taxId: '',
        works: [],
        total: 0,
        lastUpdatedAt: 0,
      };
      byKey.set(key, group);
    }

    group.works.push(w);
    group.total = group.works.length;
    if (w.updatedAt > group.lastUpdatedAt) group.lastUpdatedAt = w.updatedAt;

    // First non-empty wins — the user just wants ANY contact info,
    // not a deduplicated list.
    if (!group.phone && w.clientPhone) group.phone = w.clientPhone;
    if (!group.address && w.clientAddress) group.address = w.clientAddress;
    if (!group.taxId && w.clientTaxId) group.taxId = w.clientTaxId;
  }

  return Array.from(byKey.values());
}

export function findClientBySlug(
  groups: ReadonlyArray<ClientGroup>,
  slug: string,
): ClientGroup | undefined {
  return groups.find((g) => g.slug === slug);
}

/**
 * Address-book-anchored grouping (mobile parity).
 *
 * The list of clients shown on `/clients` must match what the user
 * sees on mobile's `ClientPickerModal` — i.e. ONLY entries the user
 * explicitly saved to the agenda (`getClients()` returns `Client[]`),
 * NOT every distinct `Work.clientName` ever typed.
 *
 * For each Client we still surface its associated active works (so
 * "12 trabajos · ayer" still works) by matching `Work.clientName` to
 * the Client's name with the same case-insensitive + NFC + trim
 * comparison `groupClients` uses. A Client with zero matching works
 * appears with `total: 0` and `lastUpdatedAt: client.updatedAt`.
 *
 * `clientId` linkage on Work is the future-proof path (`Work.clientId`
 * already exists in the type), but at the moment mobile still writes
 * client info as denormalised name/phone/address fields on Work, so
 * matching by normalised name preserves backward compatibility with
 * APKs in the wild.
 */
export function buildAgendaGroups(
  clients: ReadonlyArray<Client>,
  allWorks: ReadonlyArray<Work>,
): ClientGroup[] {
  // Index works by normalised client name for O(W + C) joining.
  const worksByKey = new Map<string, Work[]>();
  for (const w of allWorks) {
    if (w.archived) continue;
    const rawName = w.clientName?.trim();
    if (!rawName) continue;
    const key = normalizeClientKey(rawName);
    if (!key) continue;
    const list = worksByKey.get(key);
    if (list) list.push(w);
    else worksByKey.set(key, [w]);
  }

  const slugSeen = new Map<string, number>();
  const out: ClientGroup[] = [];

  for (const c of clients) {
    const rawName = c.name.trim() || '(Sin nombre)';
    const key = normalizeClientKey(rawName);
    const baseSlug = clientSlug(rawName) || 'cliente';
    const occurrence = (slugSeen.get(baseSlug) ?? 0) + 1;
    slugSeen.set(baseSlug, occurrence);
    const slug = occurrence === 1 ? baseSlug : `${baseSlug}-${occurrence}`;

    const works = worksByKey.get(key) ?? [];
    let lastUpdatedAt = c.updatedAt;
    for (const w of works) {
      if (w.updatedAt > lastUpdatedAt) lastUpdatedAt = w.updatedAt;
    }

    out.push({
      key: c.id, // stable per agenda entry — slug collision is the only risk and we handle it above
      slug,
      name: rawName,
      phone: c.phone ?? '',
      address: c.address ?? '',
      taxId: c.taxId ?? '',
      works,
      total: works.length,
      lastUpdatedAt,
    });
  }

  return out;
}
